import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useMutation, useQuery, useSubscription } from '@apollo/client';
import {
  AssetRecordType,
  DefaultColorStyle,
} from '@bigbluebutton/tldraw';
import { throttle } from 'radash';
import {
  CURRENT_PRESENTATION_PAGE_SUBSCRIPTION,
  ANNOTATION_HISTORY_STREAM,
  CURRENT_PAGE_ANNOTATIONS_STREAM,
  CURRENT_PAGE_ANNOTATIONS_QUERY,
  CURRENT_PAGE_WRITERS_SUBSCRIPTION,
} from './queries';
import {
  initDefaultPages,
  persistShape,
  notifyNotAllowedChange,
  notifyShapeNumberExceeded,
  toggleToolsAnimations,
} from './service';
import { getSettingsSingletonInstance } from '/imports/ui/services/settings';
import Auth from '/imports/ui/services/auth';
import {
  layoutSelect,
  layoutDispatch,
} from '/imports/ui/components/layout/context';
import FullscreenService from '/imports/ui/components/common/fullscreen-button/service';
import deviceInfo from '/imports/utils/deviceInfo';
import Whiteboard from './component';
import ErrorBoundaryWithReload from '../common/error-boundary/error-boundary-with-reload/component'

import useCurrentUser from '/imports/ui/core/hooks/useCurrentUser';
import {
  PRESENTATION_SET_ZOOM,
  PRES_ANNOTATION_DELETE,
  PRES_ANNOTATION_SUBMIT,
  PRESENTATION_SET_PAGE,
  PRESENTATION_PUBLISH_CURSOR,
} from '../presentation/mutations';
import { useMergedCursorData } from './hooks.ts';
import useDeduplicatedSubscription from '../../core/hooks/useDeduplicatedSubscription';
import MediaService from '/imports/ui/components/media/service';
import getFromUserSettings from '/imports/ui/services/users-settings';
import { debounce } from '/imports/utils/debounce';
import useLockContext from '/imports/ui/components/lock-viewers/hooks/useLockContext';
import useMeeting from '/imports/ui/core/hooks/useMeeting';
import { deleteWhiboardAnnotations, updateCurrentStatusPresndPage, updatePresPageZoom } from '../app/service';
import { isDesktop } from '../layout/utils';
import { removeSessionVirtualBackgroundInfo } from '../../services/virtual-background/service';
import { courseSelect } from '../app/context';

const FORCE_RESTORE_PRESENTATION_ON_NEW_EVENTS = 'bbb_force_restore_presentation_on_new_events';

const WhiteboardContainer = (props) => {
  const {
    intl,
    zoomChanger,
    fitToWidth,
  } = props;

  const WHITEBOARD_CONFIG = window.meetingClientSettings.public.whiteboard;
  const layoutContextDispatch = layoutDispatch();

  const [editor, setEditor] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [shapes, setShapes] = useState([]);
  const [removedShapes, setRemovedShapes] = useState([]);
  const [isTabVisible, setIsTabVisible] = useState(document.visibilityState === 'visible');
  const [currentPresentationPage, setCurrentPresentationPage] = useState(null);
  const courseDetails = courseSelect((state) => state);

  const { userLocks } = useLockContext();

  const { data: currentUser } = useCurrentUser((user) => ({
    presenter: user.presenter,
    isModerator: user.isModerator,
    userId: user.userId,
  }));
  const isPresenter = currentUser?.presenter;
  const isModerator = currentUser?.isModerator;

  const { data: presentationPageData, loading, error } = useDeduplicatedSubscription(
    CURRENT_PRESENTATION_PAGE_SUBSCRIPTION,
  );
  const { pres_page_curr: presentationPageArray } = (presentationPageData || {})
  const newPresentationPage = presentationPageArray && presentationPageArray[0];

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsTabVisible(document.visibilityState === 'visible');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const prevPresentationId = useRef(null);
  const waitForPresentationDone = () => {
    return new Promise((resolve) => {
      const handler = (event) => {
        if (event.detail?.isChanged) {
          window.removeEventListener("presentation-change-done", handler);
          resolve(true);
        }
      };

      window.addEventListener("presentation-change-done", handler);
    });
  };

  // useEffect(() => {
  //   if (!newPresentationPage) return;

  //   const currentId = newPresentationPage.presentationId;
  //   const prevId = prevPresentationId.current;

  //   const idChanged = prevId !== null && prevId !== currentId;

  //   // Update ref for next cycle
  //   prevPresentationId.current = currentId;

  //   // CASE 1: presentationId DID NOT CHANGE → run immediately
  //   if (!idChanged) {
  //     setCurrentPresentationPage({
  //       ...newPresentationPage,
  //       infiniteWhiteboard: true
  //     });
  //     return;
  //   }

  //   // CASE 2: presentationId CHANGED → wait
  //   console.log("presentationId changed → waiting before updating");

  //   // Ask other page to finish saving
  //   window.dispatchEvent(
  //     new CustomEvent("presentation-change", {
  //       detail: { isChangeReq: true, presentationId: currentId }
  //     })
  //   );

  //   // Wait for DONE → then update
  //   waitForPresentationDone().then(() => {
  //     console.log("DONE received → updating now");

  //     setCurrentPresentationPage({
  //       ...newPresentationPage,
  //       infiniteWhiteboard: true
  //     });
  //   });

  // }, [newPresentationPage]);


  useEffect(() => {
    if (newPresentationPage) {
      setCurrentPresentationPage({...newPresentationPage,infiniteWhiteboard:true});
    }
  }, [newPresentationPage]);



  const curPageNum = currentPresentationPage?.num;
  const curPageId = currentPresentationPage?.pageId;
  const isInfiniteWhiteboard = currentPresentationPage?.infiniteWhiteboard;

  const curPageIdRef = useRef();

  React.useEffect(() => {
    curPageIdRef.current = curPageId;
  }, [curPageId]);

  const presentationId = currentPresentationPage?.presentationId;
  const presentationName = currentPresentationPage?.presentationName


  const { data: whiteboardWritersData } = useDeduplicatedSubscription(
    CURRENT_PAGE_WRITERS_SUBSCRIPTION,
    {
      variables: { pageId: curPageId },
      skip: !curPageId,
    },
  );

  const whiteboardWriters = whiteboardWritersData?.pres_page_writers || [];
  const hasWBAccess = whiteboardWriters?.some((writer) => writer.userId === Auth.userID);

  const [presentationSetZoom] = useMutation(PRESENTATION_SET_ZOOM);
  const [presentationSetPage] = useMutation(PRESENTATION_SET_PAGE);
  const [presentationDeleteAnnotations] = useMutation(PRES_ANNOTATION_DELETE);
  const [presentationSubmitAnnotations] = useMutation(PRES_ANNOTATION_SUBMIT);
  const [presentationPublishCursor] = useMutation(PRESENTATION_PUBLISH_CURSOR);
  const deviceType = layoutSelect((i) => i.deviceType);

  const setPresentationPage = (pageId) => {
    presentationSetPage({
      variables: {
        presentationId,
        pageId,
      },
    });
  };

  const skipToSlide = (slideNum) => {
    const slideId = `${presentationId}/${slideNum}`;
    setPresentationPage(slideId);

    const apiPayload = {
      type: 'page',
      presentationId,
      pageId: slideId
    }
    updateCurrentStatusPresndPage(apiPayload)
      .then((res) => {
      }).catch((error) => {
        console.log({ error })
      })
  };

  const removeShapes = (shapeIds) => {
    const apiPayload = {
      annotationIds: shapeIds
    }
    deleteWhiboardAnnotations(apiPayload)
      .then((res) => {
        setRemovedShapes(Array.from(shapeIds || []));
      }).catch((error) => {
        console.log({ error })
      })
    presentationDeleteAnnotations({
      variables: {
        pageId: curPageIdRef.current,
        annotationsIds: shapeIds,
      },
    });
  };
  const zoomSlide = debounce((
    widthRatio, heightRatio, xOffset, yOffset, currPage = currentPresentationPage,
  ) => {
    const { pageId, num } = currPage;
    // Include standard BBB auth fields (see docs/BBB_DATA_CONTRACTS.md)
    const apiPayload = {
      internalMeetingID: Auth?._meetingID,
      externalUserID: Auth?._externUserID,
      sessionId: courseDetails?.course?.courseDetail?.courseSessionId,
      presentationId,
      pageId,
      pageNum: num,
      xOffset,
      yOffset,
      widthRatio,
      heightRatio,
    }
    updatePresPageZoom(apiPayload)
      .then((res) => {
      }).catch((error) => {
        console.log({ error })
      })
    presentationSetZoom({
      variables: {
        presentationId,
        pageId,
        pageNum: num,
        xOffset,
        yOffset,
        widthRatio,
        heightRatio,
      },
    });
  }, 500);

  const submitAnnotations = async (newAnnotations) => {


    const isAnnotationSent = await presentationSubmitAnnotations({
      variables: {
        pageId: curPageIdRef.current,
        annotations: newAnnotations,
      },
    });

    return isAnnotationSent?.data?.presAnnotationSubmit;
  };

  const persistShapeWrapper = (shape, whiteboardId, amIModerator) => {
    persistShape(shape, whiteboardId, amIModerator, submitAnnotations);
  };

  const publishCursorUpdate = useCallback((payload) => {
    const { whiteboardId, xPercent, yPercent } = payload;

    if (!whiteboardId || !xPercent || !yPercent || !(hasWBAccess || isPresenter)) return;

    presentationPublishCursor({
      variables: {
        whiteboardId,
        xPercent,
        yPercent,
      },
    });
  }, [hasWBAccess, isPresenter]);

  const throttledPublishCursorUpdate = useMemo(() => throttle(
    { interval: WHITEBOARD_CONFIG.cursorInterval },
    publishCursorUpdate,
  ), [publishCursorUpdate]);

  const isMultiUserActive = whiteboardWriters?.length > 0;

  const cursorArray = useMergedCursorData();

  const {
    data: currentMeeting,
  } = useMeeting((m) => ({
    createdTime: m.createdTime,
  }));

  const { data: initialPageAnnotations, refetch: refetchInitialPageAnnotations } = useQuery(
    CURRENT_PAGE_ANNOTATIONS_QUERY,
    {
      skip: !curPageId,
    },
  );

  const lastUpdatedAt = useMemo(() => {
    if (!initialPageAnnotations?.pres_annotation_curr?.length) {
      return currentMeeting?.createdTime
        ? new Date(currentMeeting.createdTime).toISOString()
        : null;
    }
    return initialPageAnnotations.pres_annotation_curr.reduce((latest, annotation) => {
      const updatedAt = new Date(annotation.lastUpdatedAt);
      return updatedAt > latest ? updatedAt : latest;
    }, new Date(0)).toISOString();
  }, [initialPageAnnotations]);
  const [adjustedPollShapes] = React.useState(() => new Set());
  const { data: annotationStreamData } = useSubscription(ANNOTATION_HISTORY_STREAM, {
    variables: { updatedAt: lastUpdatedAt },
    skip: !curPageId || !lastUpdatedAt,
    onData: ({ data: subscriptionData }) => {
      const annotationStream =
        subscriptionData.data?.pres_annotation_history_curr_stream || [];

      const processedAnnotationIds = new Set();
      const validShapes = [];
      const annotationsToBeRemoved = new Set();

      for (let i = annotationStream.length - 1; i >= 0; i--) {
        const annotation = annotationStream[i];
        const { annotationId, annotationInfo } = annotation;

        if (processedAnnotationIds.has(annotationId)) {
          continue;
        }

        processedAnnotationIds.add(annotationId);

        if (!annotationInfo) {
          annotationsToBeRemoved.add(annotationId);
        } else {
          // validShapes.push({ ...annotationInfo, id: annotationId });
          let shape = { ...annotationInfo, id: annotationId };

          // ✅ Check if shape is poll-result and not adjusted before
          if (
            annotationId.startsWith("shape:poll-result") &&
            !adjustedPollShapes.has(annotationId)
          ) {
            shape = { ...shape, y: 200 }; // shift Y
            adjustedPollShapes.add(annotationId);   // mark as adjusted
          }

          validShapes.push(shape);
        }
      }

      setShapes(() => {
        if (validShapes.length > 0) {
          const restoreOnUpdate = getFromUserSettings(
            FORCE_RESTORE_PRESENTATION_ON_NEW_EVENTS,
            window.meetingClientSettings.public.presentation.restoreOnUpdate
          );

          if (restoreOnUpdate) {
            MediaService.setPresentationIsOpen(layoutContextDispatch, true);
          }
        }

        return validShapes.length > 0 ? validShapes : [];
      });
      setRemovedShapes(Array.from(annotationsToBeRemoved || []));
    },
  });
  useEffect(() => {
    if (isTabVisible && curPageId) {
      refetchInitialPageAnnotations();
    }
  }, [isTabVisible, curPageId, presentationId, fitToWidth]);

  const processAnnotations = (data) => {
    let annotationsToBeRemoved = [];
    const newAnnotations = [];
    const updatedAnnotations = [];

    data.forEach((item) => {
      if (!item.annotationInfo) {
        annotationsToBeRemoved.push(item.annotationId);
      } else {
        const annotationInfoParsed = JSON.parse(item.annotationInfo);
        const existingShape = editor?.getShape(item.annotationId);
        if (existingShape) {
          updatedAnnotations.push({
            ...item,
            annotationInfo: annotationInfoParsed,
          });

          annotationsToBeRemoved = annotationsToBeRemoved.filter(
            (id) => id !== item.annotationId
          );
        } else {
          newAnnotations.push({
            ...item,
            annotationInfo: annotationInfoParsed,
          });
        }
      }
    });

    setShapes(() => [
      ...updatedAnnotations.map(({ annotationInfo, annotationId }) => ({
        ...annotationInfo,
        id: annotationId,
      })),
      ...newAnnotations.map(({ annotationInfo, annotationId }) => ({
        ...annotationInfo,
        id: annotationId,
      })),
    ]);

    if (updatedAnnotations.length > 0 || newAnnotations.length > 0) {
      const restoreOnUpdate = getFromUserSettings(
        FORCE_RESTORE_PRESENTATION_ON_NEW_EVENTS,
        window.meetingClientSettings.public.presentation.restoreOnUpdate,
      );

      if (restoreOnUpdate) {
        MediaService.setPresentationIsOpen(layoutContextDispatch, true);
      }
    }

    setRemovedShapes(annotationsToBeRemoved.length > 0 ? annotationsToBeRemoved : []);
  };

  useEffect(() => {
    if (initialPageAnnotations && initialPageAnnotations.pres_annotation_curr) {
      processAnnotations(initialPageAnnotations.pres_annotation_curr);
    }
  }, [initialPageAnnotations]);

  useEffect(() => {
    if (!curPageId || !lastUpdatedAt) {
      setShapes([]);
      setRemovedShapes([]);
    }
  }, [curPageId, lastUpdatedAt]);

  const resetPointerToMouse = (editor) => {
    const currentTool = editor?.current?.getCurrentToolId();
    editor?.current?.setCurrentTool('select');
    if (currentTool !== 'select') {
      setTimeout(() => editor?.current?.setCurrentTool(currentTool), 0);
    }
  };
  useEffect(() => {
    if (!editor) return;
    const SMALL_BOUND_THRESHOLD = 2;
    const handlePointerUp = (e) => {
      // Example usage:

      if (e.pointerType === 'pen') {
        editor?.updateInstanceState({ isPenMode: false })

        // const smallShapes = editor
        //   .getCurrentPageShapes()
        //   .filter((shape) => {
        //     if (shape.type !== 'draw') return false;
        //     const bounds = editor.getShapePageBounds(shape);
        //     const w = bounds.maxX - bounds.minX;
        //     const h = bounds.maxY - bounds.minY;
        //     return w < SMALL_BOUND_THRESHOLD || h < SMALL_BOUND_THRESHOLD;
        //   });

        // editor.deleteShapes(smallShapes.map((s) => s.id));
      }
    };
    window.addEventListener('pointerup', handlePointerUp, { passive: false });
    return () => window.removeEventListener('pointerup', handlePointerUp, { passive: false });
  }, [editor]);


  useEffect(() => {
    if (!editor) return;

    const handleTextShapeFix = debounce(() => {
      const selectedShapeIds = editor.getSelectedShapeIds();
      const editingShapeId = editor.getEditingShapeId();

      for (const id of selectedShapeIds) {
        const shape = editor.getShape(id);

        // Only patch if it's a text shape and NOT being edited
        if (
          shape?.type === 'text' &&
          shape.props.autoSize
        ) {

          editor.updateShape({
            id: shape.id,
            type: shape.type,
            props: {
              ...shape.props,
              w: 500,
              autoSize: false,
            },
          });
        }


      }
    }, 300); // only patch after 300ms pause
    // const cleanup = editor.on('change', handleTextShapeFix);
    const unsub = editor.on('change', handleTextShapeFix);
    return () => {
      if (typeof unsub === 'function') unsub();
      else if (typeof unsub?.dispose === 'function') unsub.dispose();
      handleTextShapeFix.cancel?.();
    };
  }, [editor]);

  useEffect(() => {
    if (!editor) return
    const handleKeydown = (e) => {
      if (e.key === 'Escape' && editor.getInstanceState().isCropping) {
        editor.setCroppingShape(null)
        editor.setCurrentTool('select.idle')
      }
    }

    const handleClickOutside = (e) => {
      const croppingId = editor.getCroppingShapeId()
      const toolId = editor.getCurrentToolId()
      // REMOVE BLACK COLOR FOR HIGHLIGHT
      if (toolId === 'highlight') {
        setTimeout(() => {
          const buttons = document.getElementsByClassName(
            "tlui-button tlui-button__icon tlui-button-grid__button"
          )
          Array.from(buttons).forEach((btn) => {
            if (btn.getAttribute("data-id") === "black") {
              btn.style.display = "none"
            }
          })
        }, 300)
      }

      if (!croppingId) return


      const clickedShape = editor.getShapeAtPoint({ x: e.clientX, y: e.clientY })

      const isCropHandle = toolId?.includes('crop')
      const isClickedImage = clickedShape?.id === croppingId
      // const isClickedBG = clickedShape?.id === 'shape:BG-1'
      const isClickedBG = clickedShape?.id?.startsWith('shape:BG-');
      const isClickedBoundary = clickedShape?.id?.startsWith('shape:BOUNDARY-');


      if (isCropHandle || !isClickedBG || !isClickedBoundary) {
        // Allow crop interaction
        return
      }

      // Clicked elsewhere → exit crop
      editor.setCroppingShape(null)
      editor.setCurrentTool('select.idle')
    }

    const exitCrop = () => {
      editor.setCroppingShape(null)
      editor.setCurrentTool('select.idle')
    }

    window.addEventListener('keydown', handleKeydown)
    // window.addEventListener('mousedown', handleClickOutside)

    return () => {
      window.removeEventListener('keydown', handleKeydown)
      // window.removeEventListener('mousedown', handleClickOutside)
    }
  }, [editor])

  useEffect(() => {

    const handlePointerDown = (event) => {
      if (!editor) return
      const point = editor.screenToPage({ x: event.clientX, y: event.clientY })

      const shapes = editor.getShapesAtPoint(point, {
        hitInside: true,
        margin: 2,
      })

      // ✅ Prefer non-BG shapes first
      const nonBgShape = shapes.find(s => !s.id.startsWith('shape:BG'))
      const bgShape = shapes.find(s => s.id.startsWith('shape:BG'))

      const shape = nonBgShape || bgShape // use BG only if nothing else found
      const isBgShape = shape?.id?.startsWith('shape:BG')
      const isBoundaryShape = shape?.id?.startsWith('shape:BOUNDARY')

      if (isBgShape || isBoundaryShape) return

      // Example: right-click handling

      if (!shape) return

      // ✅ If BG is clicked → unselect everything
      if (shape.id.startsWith('shape:BG') || shape.id.startsWith('shape:BOUNDARY')) {
        editor.selectNone()
        event.stopPropagation()
        event.preventDefault()
        return
      }

      // ✅ If shape is locked or device is touch → allow only selection
      if (shape?.isLocked || deviceType !== 'desktop' || !isBgShape || !isBoundaryShape) {
        editor.select(shape.id)
        event.stopPropagation()
        event.preventDefault()
        return
      }
      if (event.button == 2) {
        if (editor.getEditingShapeId?.()) editor.setEditingShape(null)
        editor.select(shape.id)
        return
      }

    }

    window?.addEventListener('pointerup', handlePointerDown, { passive: false })

    return () => {
      window?.removeEventListener('pointerup', handlePointerDown, { passive: false })
    }
  }, [editor, deviceType])


  const bgShape = [];

  const rectShape = [];

  const { isIphone, isPhone } = deviceInfo;

  const assetId = AssetRecordType.createId(curPageNum);
  const assets = [{
    id: assetId,
    typeName: 'asset',
    type: 'image',
    meta: {},
    props: {
      w: currentPresentationPage?.scaledWidth,
      h: currentPresentationPage?.scaledHeight,
      src: currentPresentationPage?.svgUrl,
      name: '',
      isAnimated: false,
      mimeType: null,
    },
  }];

  const Settings = getSettingsSingletonInstance();
  const { isRTL } = Settings.application;
  const width = layoutSelect((i) => i?.output?.presentation?.width);
  const height = layoutSelect((i) => i?.output?.presentation?.height);
  const sidebarNavigationWidth = layoutSelect(
    (i) => i?.output?.sidebarNavigation?.width,
  );
  const { maxStickyNoteLength, maxNumberOfAnnotations } = WHITEBOARD_CONFIG;
  const fontFamily = WHITEBOARD_CONFIG.styles.text.family;
  const fontStyle = 'sans'
  const {
    colorStyle, fillStyle, sizeStyle
  } = WHITEBOARD_CONFIG.styles;

  const dashStyle = 'solid'
  // const sizeStyle = 's'

  const handleToggleFullScreen = (ref) => FullscreenService.toggleFullScreen(ref);

  // Center the background shape perfectly within the presentation area
  // Use simple centering approach for Tldraw's coordinate system
  const bgWidth = (currentPresentationPage?.scaledWidth ?? 1);
  const bgHeight = (currentPresentationPage?.scaledHeight ?? 1);

  // Calculate proper center coordinates for Tldraw's coordinate system
  // The shape should be positioned so its center is at (0,0) in Tldraw's world space
  const centerOffsetX = -bgWidth / 2;
  const centerOffsetY = -bgHeight / 2;

  const zoomCamera = 0.4542
  bgShape.push({
    x: centerOffsetX,  // Center horizontally using calculated offset
    y: centerOffsetY,  // Center vertically using calculated offset
    rotation: 0,
    isLocked: true,
    opacity: 1,
    meta: {},
    id: `shape:BG-${curPageNum}`,
    type: 'image',
    props: {
      w: bgWidth,
      h: bgHeight,
      assetId,
      playing: true,
      url: '',
      crop: null,
    },
    parentId: `page:${curPageNum}`,
    index: 'a0',
    typeName: 'shape',
  });


  const maxArea = 4000
  rectShape.push({
    id: `shape:BOUNDARY-${curPageNum}}`,
    type: "geo",
    x: -(maxArea / 2), // start from top-left corner
    y: -(maxArea / 2), // start from top-left corner
    rotation: 0,
    isLocked: true,
    opacity: 1,
    parentId: `page:${curPageNum}`,
    index: "a1",
    meta: {
      synced: true,
      createdBy: "system",
    },
    props: {
      h: maxArea,
      w: maxArea,
      geo: "rectangle",
      url: "",
      dash: "dashed",
      fill: "none",
      font: "sans",
      size: "s",
      text: "Only content inside the boundary is included in the download prsentation.",
      align: "start",
      color: "grey",
      growY: 0,
      labelColor: "black",
      verticalAlign: "start"
    },
    typeName: "shape",
  }
  )

  if (!currentPresentationPage) return null;

  return (
    <ErrorBoundaryWithReload>
      <Whiteboard
        key={presentationId}
        {...{
          isPresenter,
          isModerator,
          currentUser,
          isRTL,
          width,
          height,
          maxStickyNoteLength,
          maxNumberOfAnnotations,
          fontFamily,
          colorStyle,
          dashStyle,
          fillStyle,
          fontStyle,
          sizeStyle,
          handleToggleFullScreen,
          sidebarNavigationWidth,
          layoutContextDispatch,
          initDefaultPages,
          persistShapeWrapper,
          isMultiUserActive,
          shapes,
          removedShapes,
          bgShape,
          assets,
          removeShapes,
          zoomSlide,
          notifyNotAllowedChange,
          notifyShapeNumberExceeded,
          whiteboardToolbarAutoHide: Settings?.application?.whiteboardToolbarAutoHide,
          animations: Settings?.application?.animations,
          toggleToolsAnimations,
          isIphone,
          isPhone,
          currentPresentationPage,
          numberOfPages: currentPresentationPage?.totalPages,
          presentationId,
          hasWBAccess,
          whiteboardWriters,
          zoomChanger,
          skipToSlide,
          locale: Settings?.application?.locale,
          darkTheme: Settings?.application?.darkTheme,
          selectedLayout: Settings?.application?.selectedLayout,
          isInfiniteWhiteboard,
          curPageNum,
          setEditor,
          rectShape,
          courseDetails,
          presentationName
        }}
        {...props}
        meetingId={Auth.meetingID}
        publishCursorUpdate={throttledPublishCursorUpdate}
        otherCursors={cursorArray}
        hideViewersCursor={userLocks?.hideViewersCursor}
      />
    </ErrorBoundaryWithReload>
  );
};

WhiteboardContainer.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  zoomChanger: PropTypes.func.isRequired,
};

export default WhiteboardContainer;
